import type { CompanyAccountModelDto, LookupModelDto } from '@treasury/api/channel';
import TransferPaymentsService from '@treasury/domain/channel/services/transfers/transfer-payments-service';
import { TmBaseComponent } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-currency-range';
// eslint-disable-next-line no-duplicate-imports
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { mapCurrencyRangeToDto } from '@treasury/presentation/components/tm-currency-range.mappings';
import '@treasury/presentation/components/tm-date-range';

import { DeepReactive } from '@jack-henry/frontend-utils/lit';
// eslint-disable-next-line no-duplicate-imports
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { mapDateRangeToDto } from '@treasury/presentation/components/tm-date-range.mappings';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-segmented-control';
import '@treasury/presentation/components/tm-text-field';
import { add } from 'date-fns';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import {
    mapTransferPaymentStatus,
    TransferPaymentStatus,
    transferPaymentStatuses,
} from '../../transfers/data/transfer-payment-status-items.types';
import {
    getPaymentOptions,
    mapPaymentOptionToVm,
    PaymentOption,
} from '../data/payment-option-item';

/**
 * Lookup model without extraneous fields.
 */
export type CorrectedLookupModel = Omit<LookupModelDto, 'id' | 'updatedDate'>;

const defaultTransferDateRange: DateRange = {
    rangeType: DateRangeType.DateRange,
    startDate: new Date(),
    endDate: add(new Date(), { weeks: 1 }),
};

const defaultCreatedDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultState = {
    transferDate: true,
    status: true,
    fromAccount: true,
    toAccount: true,
    amountRange: true,
    createdDate: true,
    paymentOption: true,
};

const accountOperatorOptions = [
    {
        label: 'Both',
        value: 'FromTo',
    },
    {
        label: 'From',
        value: 'From',
    },
    {
        label: 'To',
        value: 'To',
    },
];

export const tagName = 'loan-payments-filter-sheet';
@customElement(tagName)
export class LoanPaymentsFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @state()
    private transferDateRange: DateRange = defaultTransferDateRange;

    @state()
    private statuses: TransferPaymentStatus[] = [...transferPaymentStatuses];

    @state()
    private statusesSelected: TransferPaymentStatus[] = [...this.statuses];

    @state()
    private paymentOptions = getPaymentOptions();

    @state()
    private paymentOptionsSelected: PaymentOption[] = [...this.paymentOptions];

    @state()
    private accounts: CompanyAccountModelDto[] = [];

    @state()
    private fromAccountsSelected: CompanyAccountModelDto[] = [];

    @state()
    private toAccountsSelected: CompanyAccountModelDto[] = [];

    @state()
    private accountOperatorSelected = accountOperatorOptions[0];

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @state()
    private createdDateRange: DateRange = defaultCreatedDateRange;

    @state()
    private transactionId?: string;

    @state()
    private loading = true;

    @DeepReactive()
    private validState = defaultState;

    protected async firstUpdated() {
        try {
            this.accounts = await TransferPaymentsService.fetchTransferAccounts();
            this.onResetForm();
            this.onApplyFilters();
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        return html`
            <tm-bottom-sheet
                .open=${this.open}
                @close=${() => {
                    this.open = false;
                    this.dispatchEvent(new CustomEvent('close'));
                }}
                ><p slot="header-center">Filter Loan Payments</p>
                <p slot="header-right"></p>
                <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
                <tm-footer
                    .buttonConfig=${[
                        {
                            text: 'Apply Filters',
                            onClick: () => {
                                this.onApplyFilters();
                            },
                            disabled: Object.values(this.validState).some(isValid => !isValid),
                        },
                        {
                            importance: 'secondary',
                            text: 'Reset',
                            onClick: () => {
                                this.onResetForm();
                            },
                        },
                    ] as ButtonConfig[]}
                >
                </tm-footer>
            </tm-bottom-sheet>
        `;
    }

    private onApplyFilters() {
        this.open = false;
        this.dispatchEvent(new CustomEvent('close'));
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.toDto(),
            })
        );
    }

    private onResetForm() {
        this.transferDateRange = { ...defaultTransferDateRange };
        this.statusesSelected = this.statuses;
        this.paymentOptionsSelected = this.paymentOptions;
        [this.accountOperatorSelected] = accountOperatorOptions;
        this.fromAccountsSelected = [...this.accounts];
        this.toAccountsSelected = [...this.accounts];
        this.amountRange = { ...defaultAmountRange };
        this.createdDateRange = { ...defaultCreatedDateRange };
        this.transactionId = undefined;
        this.validState = { ...defaultState };
    }

    /**
     * This should return a  `InternalTransferTransactionFilterModelDto`,
     * but the generated types have extraneous required fields that are not needed here.
     */
    private toDto() {
        const {
            operator: transferDateOperator,
            dateFrom: transferDateFrom,
            dateTo: transferDateTo,
        } = mapDateRangeToDto(this.transferDateRange);

        const {
            operator: createdDateOperator,
            dateFrom: createdDateFrom,
            dateTo: createdDateTo,
        } = mapDateRangeToDto(this.createdDateRange);

        const {
            operator: amountOperator,
            fromAmount,
            toAmount,
        } = mapCurrencyRangeToDto(this.amountRange);

        const paymentOptionType: CorrectedLookupModel[] = this.paymentOptionsSelected
            .map(mapPaymentOptionToVm)
            .map(vm => ({
                value: vm.label,
                key: vm.value.toString(),
            }));

        const status: CorrectedLookupModel[] = this.statusesSelected
            .map(mapTransferPaymentStatus)
            .map(vm => ({
                value: vm.label,
                key: vm.value,
            }));

        return {
            transferDateOperator,
            transferDateFrom,
            transferDateTo,
            status,
            paymentOptionType,
            accountOperator: this.accountOperatorSelected.value,
            fromAccounts: [...this.fromAccountsSelected],
            toAccounts: [...this.toAccountsSelected],
            amountOperator,
            fromAmount,
            toAmount,
            createdDateOperator,
            createdDateFrom,
            createdDateTo,
            transactionId: this.transactionId,
        };
    }

    private renderForm() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`
            <form>
                <tm-date-range
                    label="Payment Date"
                    required
                    .initial=${this.transferDateRange}
                    @value-changed=${(e: TmValueChanged<DateRange>) => {
                        const { value, isValid } = e.detail;

                        this.validState.transferDate = isValid;
                        if (value) {
                            this.transferDateRange = value;
                        }
                    }}
                ></tm-date-range>
                <tm-multi-select
                    label="Status"
                    .allowSelectAll=${true}
                    .allSelectedText=${'All Statuses'}
                    .items=${this.statuses}
                    .initial=${[...this.statuses]}
                    .mapItem=${mapTransferPaymentStatus}
                    @value-changed=${(e: TmValueChanged<TransferPaymentStatus[]>) => {
                        this.statusesSelected = e.detail.value || [];
                        this.validState.status = this.statusesSelected.length > 0;
                    }}
                ></tm-multi-select>
                <tm-segmented-control
                    class="py-2"
                    label="Accounts"
                    .options=${accountOperatorOptions}
                    .initial=${this.accountOperatorSelected}
                    @value-changed=${({ detail }: TmValueChanged<SelectItemVm<string>>) => {
                        if (!detail.value) {
                            return;
                        }
                        this.accountOperatorSelected = detail.value;
                        switch (this.accountOperatorSelected.value) {
                            case 'From':
                                this.validState.toAccount = true;
                                this.validState.fromAccount = this.fromAccountsSelected.length > 0;
                                break;
                            case 'To':
                                this.validState.fromAccount = true;
                                this.validState.toAccount = this.toAccountsSelected.length > 0;
                                break;
                            default:
                                this.validState.fromAccount = this.fromAccountsSelected.length > 0;
                                this.validState.toAccount = this.toAccountsSelected.length > 0;
                        }
                    }}
                ></tm-segmented-control>
                <tm-multi-select
                    class=${classMap({
                        hidden: this.accountOperatorSelected.value === 'To',
                    })}
                    label="From Account"
                    .allowSelectAll=${true}
                    .allSelectedText=${'All Accounts'}
                    .items=${this.accounts}
                    .initial=${[...this.fromAccountsSelected]}
                    .mapItem=${mapAccountToVm}
                    @value-changed=${(e: TmValueChanged<CompanyAccountModelDto[]>) => {
                        this.fromAccountsSelected = e.detail.value || [];
                        this.validState.fromAccount = this.fromAccountsSelected.length > 0;
                    }}
                ></tm-multi-select>
                <tm-multi-select
                    class=${classMap({
                        hidden: this.accountOperatorSelected.value === 'From',
                    })}
                    label="To Account"
                    .allowSelectAll=${true}
                    .allSelectedText=${'All Accounts'}
                    .items=${this.accounts}
                    .initial=${[...this.toAccountsSelected]}
                    .mapItem=${mapAccountToVm}
                    @value-changed=${(e: TmValueChanged<CompanyAccountModelDto[]>) => {
                        this.toAccountsSelected = e.detail.value || [];
                        this.validState.toAccount = this.toAccountsSelected.length > 0;
                    }}
                ></tm-multi-select>
                <tm-currency-range
                    .initial=${this.amountRange}
                    @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                        const { isValid, value } = e.detail;
                        this.validState.amountRange = isValid;
                        if (!value) {
                            return;
                        }
                        this.amountRange = value;
                    }}
                ></tm-currency-range>
                <tm-multi-select
                    label="Payment Option"
                    .allowSelectAll=${true}
                    .allSelectedText=${'All Options'}
                    .items=${this.paymentOptions}
                    .initial=${[...this.paymentOptionsSelected]}
                    .mapItem=${mapPaymentOptionToVm}
                    @value-changed=${(e: TmValueChanged<PaymentOption[]>) => {
                        this.paymentOptionsSelected = e.detail.value || [];
                        this.validState.paymentOption = this.paymentOptionsSelected.length > 0;
                    }}
                ></tm-multi-select>
                <tm-text-field
                    label="Transaction ID"
                    .initial=${this.transactionId}
                    @value-changed=${(e: TmValueChanged<string>) => {
                        const { value } = e.detail;
                        if (value) {
                            this.transactionId = value;
                        }
                    }}
                ></tm-text-field>
                <tm-date-range
                    label="Created Date"
                    .initial=${this.createdDateRange}
                    @value-changed=${(e: TmValueChanged<DateRange>) => {
                        const { isValid, value } = e.detail;
                        this.validState.createdDate = isValid;
                        if (value) {
                            this.createdDateRange = value;
                        }
                    }}
                    .maxDate=${new Date()}
                ></tm-date-range>
            </form>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                }
            `,
        ];
    }
}

function mapAccountToVm(account: CompanyAccountModelDto): SelectItemVm<CompanyAccountModelDto> {
    return {
        label: account.accountDisplayLabel ?? '',
        value: account,
    };
}
declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanPaymentsFilterSheet;
    }
}
